import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { ForwardedRef, forwardRef, useEffect, useRef, useState, type JSX } from 'react';
import "../../assets/css/elynx-ag-grid.css";
import { numberComparator } from "../ag-grid-extensions/comparators/NumberComparator";
import { textComparator } from "../ag-grid-extensions/comparators/TextComparator";
import { timestampComparator } from "../ag-grid-extensions/comparators/TimestampComparator";
import { numericColumnFilter } from "../ag-grid-extensions/filters/NumericColumnFilter";
import { textColumnFilter } from "../ag-grid-extensions/filters/TextColumnFilter";
import { timestampColumnFilter } from "../ag-grid-extensions/filters/TimestampColumnFilter";
import HeaderCell from "./HeaderCell";
import InternalGrid, { AutoSizeStrategyEnum, InternalGridProps } from "./InternalGrid";

const debounceDelay = 250; // milliseconds

export type DefaultGridProps<TData = any> = InternalGridProps<TData>;

export type DefaultGridRef<TData = any> = AgGridReact<TData>;

const columnTypes = {
    text: {
        comparator: textComparator,
        ...textColumnFilter
    },
    number: {
        comparator: numberComparator,
        ...numericColumnFilter
    },
    timestamp: {
        comparator: timestampComparator,
        ...timestampColumnFilter
    },
    multiline: {
        autoHeight: true,
        wrapText: true,
        cellStyle: {
            lineHeight: "normal",
            paddingTop: "var(--ag-spacing)",
            paddingBottom: "var(--ag-spacing)",
        }
    }
}

function DefaultGrid<TData = any>({
    showFooter,
    spacingOverride,
    fontSizeOverride,
    initialMinWidth,
    autoSizeStrategy = AutoSizeStrategyEnum.FitToCellContents,
    defaultColDef,
    columnDefs,
    rowData,
    ...props
}: DefaultGridProps<TData>, ref: ForwardedRef<DefaultGridRef<TData>>) {

    // Debug
    // useEffect(() => {
    //     console.log("#### Created DefaultGrid ####\t");
    //     return () => console.log("#### Destroyed DefaultGrid ####");
    // }, []);

    const [columns, setColumns] = useState<ColDef[]>();
    const [rows, setRows] = useState<TData[]>();
    const timeout = useRef<NodeJS.Timeout | undefined>(undefined);

    useEffect(() => {

        function setData() {
            
            // Debug
            console.log(
                "#### ===================================== ####\n" +
                "#### ========> Setting Grid Data <======== ####\n" +
                "#### ===================================== ####"
            );

            setColumns(
                (columnDefs ?? []).map((colDef) => ({
                    ...colDef,
                    headerTooltip: colDef.headerName,
                }))
            );
            setRows(rowData ?? []);
        }

        if (timeout.current) {
            clearTimeout(timeout.current);
        }

        timeout.current = setTimeout(() => {
            setData();
        }, debounceDelay);

        // Debug
        console.log(
            `#### Debouncing Grid ####\n` +
            `\ttimeout=${timeout.current}`
        );
    }, [
        columnDefs,
        rowData
    ])

    // Debug
    // console.log(`#### DefaultGrid ####\n` +
    //     `\tColumns: ${columns?.length}\n` +
    //     `\tRows: ${rows?.length}`
    // );

    return (
        <>
            {(columns?.length ?? 0) > 0 && (rows?.length ?? 0) > 0 &&
                <InternalGrid<TData>
                    {...props}
                    ref={ref}
                    animateRows={false}
                    maintainColumnOrder={true}
                    skipHeaderOnAutoSize={false}
                    suppressHorizontalScroll={false}
                    suppressColumnVirtualisation={true}
                    embedFullWidthRows={true}
                    groupMaintainOrder={true}
                    defaultColDef={{
                        ...defaultColDef,
                        autoHeaderHeight: true,
                        wrapHeaderText: true,
                        headerComponent: HeaderCell
                    }}
                    columnDefs={columns}
                    columnTypes={columnTypes}
                    rowData={rows}
                    grandTotalRow={
                        showFooter
                            ? "bottom"
                            : undefined
                    }
                    autoSizeStrategy={autoSizeStrategy}
                    initialMinWidth={initialMinWidth ?? 100}
                    spacingOverride={spacingOverride}
                    fontSizeOverride={fontSizeOverride}
                />
            }
        </>
    );
}

export default forwardRef(DefaultGrid) as <TData>(props: DefaultGridProps<TData> & { ref?: ForwardedRef<DefaultGridRef<TData>>; }) => JSX.Element;